import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalBody, Input, FormGroup, Label, Row, Col, Button } from "reactstrap";
import { ClipLoader } from "react-spinners";

import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import { addCalendarDays, formatDateLabel, toDateInputValue } from "./dateUtils";
import { listarAnexos } from "../../../services/freteTabelaAnexosApi";

const inputStyle = {
  background: "var(--everlog-card-surface-field)",
  border: "1px solid var(--everlog-card-border)",
  color: "var(--everlog-card-title)",
};

const labelStyle = { fontSize: "0.82rem", color: "var(--everlog-card-muted)", marginBottom: "0.25rem" };

function formatDate(val) {
  return toDateInputValue(val);
}

function displayDate(val) {
  if (!val) return "";
  return formatDateLabel(val);
}

export default function ReajusteModal({ onClose, initialTabelaId }) {
  const { empresa, transportadora } = useCompanies();
  const empresaId = empresa?.value;
  const transportadoraId = transportadora?.value;

  // ─── tabela selection ──────────────────────────────────────────────────────
  const [tabelas, setTabelas] = useState([]);
  const [loadingTabelas, setLoadingTabelas] = useState(false);
  const [busca, setBusca] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [selectedTabela, setSelectedTabela] = useState(null);
  const [initialAutoSelected, setInitialAutoSelected] = useState(false);

  // ─── reajuste params ───────────────────────────────────────────────────────
  const [nomeNova, setNomeNova] = useState("");
  const [percentual, setPercentual] = useState("");
  const [dataValIni, setDataValIni] = useState("");
  const [dataValFim, setDataValFim] = useState("");
  const [atualizarDataFimOrigem, setAtualizarDataFimOrigem] = useState(false);

  // ─── taxas ─────────────────────────────────────────────────────────────────
  const [reajustarTaxas, setReajustarTaxas] = useState(false);
  const [taxasDisponiveis, setTaxasDisponiveis] = useState([]);
  const [loadingTaxas, setLoadingTaxas] = useState(false);
  const [taxaConfig, setTaxaConfig] = useState({}); // { [idFreteTaxa]: { selected: bool, percentual: "" } }

  // ─── status ────────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState(null);

  // PO1-2859: anexos da tabela de origem (o backend copia todos para a tabela nova).
  const [anexosOrigem, setAnexosOrigem] = useState(null);
  useEffect(() => {
    let ativo = true;
    setAnexosOrigem(null);
    if (!selectedId) return undefined;
    listarAnexos(selectedId)
      .then((lista) => { if (ativo) setAnexosOrigem(lista); })
      .catch(() => { if (ativo) setAnexosOrigem(null); });
    return () => { ativo = false; };
  }, [selectedId]);

  const buscarTabelas = useCallback(async (nome) => {
    if (!empresaId) return;
    setLoadingTabelas(true);
    try {
      const params = {
        idEmbarcador: empresaId,
        limit: 50,
        offset: 0,
        status: 1,
      };
      if (transportadoraId) params.idTransportadora = transportadoraId;
      if (nome) params.nome = nome;
      const res = await api.get("/frete/tabelas", { params });
      setTabelas(res.data.rows || []);
    } catch (e) {
      // silent
    } finally {
      setLoadingTabelas(false);
    }
  }, [empresaId, transportadoraId]);

  useEffect(() => { buscarTabelas(""); }, [buscarTabelas]);

  useEffect(() => {
    if (!initialTabelaId || initialAutoSelected) return;
    if (!Array.isArray(tabelas) || tabelas.length === 0) return;
    const found = tabelas.find((t) => String(t.id) === String(initialTabelaId));
    if (found) {
      setInitialAutoSelected(true);
      handleSelectTabela(initialTabelaId);
    } else {
      (async () => {
        try {
          const res = await api.get(`/frete/tabelas/${initialTabelaId}`);
          const det = res.data;
          if (det && det.id) {
            setTabelas((prev) => {
              const exists = prev.some((t) => String(t.id) === String(det.id));
              return exists ? prev : [det, ...prev];
            });
            setInitialAutoSelected(true);
            setSelectedId(det.id);
            setSelectedTabela(det);
            setNomeNova(det.nome || "");
            setDataValIni(det.dataValFim ? addCalendarDays(det.dataValFim, 1) : "");
            try {
              const r2 = await api.get(`/frete/tabelas/${det.id}/taxas-tipos`);
              const lista = Array.isArray(r2.data) ? r2.data : [];
              setTaxasDisponiveis(lista);
              const cfg = {};
              lista.forEach((tx) => { cfg[tx.value] = { selected: true, percentual: "" }; });
              setTaxaConfig(cfg);
            } catch (_) {
              setTaxasDisponiveis([]);
            }
          }
        } catch (_) {
          // silent
        }
      })();
    }
  }, [initialTabelaId, tabelas, initialAutoSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSelectTabela(id) {
    setSelectedId(id);
    const t = tabelas.find((r) => String(r.id) === String(id));
    setSelectedTabela(t || null);
    setNomeNova(t ? t.nome : "");
    setPercentual("");
    setDataValIni(t?.dataValFim ? addCalendarDays(t.dataValFim, 1) : "");
    setDataValFim("");
    setAtualizarDataFimOrigem(false);
    setReajustarTaxas(false);
    setTaxasDisponiveis([]);
    setTaxaConfig({});
    setErro(null);

    if (id) {
      setLoadingTaxas(true);
      try {
        const res = await api.get(`/frete/tabelas/${id}/taxas-tipos`);
        const lista = Array.isArray(res.data) ? res.data : [];
        setTaxasDisponiveis(lista);
        const cfg = {};
        lista.forEach((tx) => { cfg[tx.value] = { selected: true, percentual: "" }; });
        setTaxaConfig(cfg);
      } catch (e) {
        setTaxasDisponiveis([]);
      } finally {
        setLoadingTaxas(false);
      }
    }
  }

  function setTaxaSelected(id, selected) {
    setTaxaConfig((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { percentual: "" }), selected },
    }));
  }

  function setTaxaPercentual(id, valor) {
    setTaxaConfig((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { selected: true }), percentual: valor },
    }));
  }

  function toggleTodasTaxas(selected) {
    setTaxaConfig((prev) => {
      const next = { ...prev };
      taxasDisponiveis.forEach((tx) => {
        next[tx.value] = { ...(next[tx.value] || { percentual: "" }), selected };
      });
      return next;
    });
  }

  function aplicarPercCustosTodasTaxas() {
    setTaxaConfig((prev) => {
      const next = { ...prev };
      taxasDisponiveis.forEach((tx) => {
        next[tx.value] = { ...(next[tx.value] || { selected: true }), percentual: "" };
      });
      return next;
    });
  }

  const selectionBlocked = selectedTabela && !selectedTabela.dataValFim;
  const paramsLocked = !selectedTabela || selectionBlocked;
  const novaDataFimOrigem = dataValIni ? addCalendarDays(dataValIni, -1) : "";
  const dataFinalInvalida =
    dataValFim &&
    dataValIni &&
    dataValFim < dataValIni;
  const canExecute =
    !saving &&
    !selectionBlocked &&
    !!selectedId &&
    !!nomeNova.trim() &&
    percentual !== "" &&
    !!dataValIni &&
    !!dataValFim &&
    !dataFinalInvalida;

  async function handleReajustar() {
    setErro(null);
    if (!selectedId) return setErro("Selecione uma tabela de frete.");
    if (!selectedTabela?.dataValFim) return setErro("A tabela selecionada não possui data final de vigência. Defina a data final na tabela antes de reajustar.");
    if (!nomeNova.trim()) return setErro("Informe o nome da nova tabela.");
    if (percentual === "" || percentual === null) return setErro("Informe o percentual de reajuste.");
    if (!dataValIni) return setErro("Informe a data inicial de vigência da nova tabela.");
    if (!dataValFim) return setErro("Informe a data final de vigência da nova tabela.");
    if (dataValFim < dataValIni) return setErro("A data final da nova tabela deve ser igual ou posterior à data inicial.");

    let reajusteTaxas = [];
    if (reajustarTaxas) {
      const percCustos = parseFloat(percentual);
      reajusteTaxas = taxasDisponiveis
        .filter((tx) => taxaConfig[tx.value]?.selected)
        .map((tx) => {
          const percTaxa = taxaConfig[tx.value]?.percentual;
          const percFinal = percTaxa === "" || percTaxa == null
            ? percCustos
            : parseFloat(percTaxa);
          return { idFreteTaxa: tx.value, percentual: percFinal };
        })
        .filter((t) => !Number.isNaN(t.percentual));
      if (reajusteTaxas.length === 0) {
        return setErro("Selecione ao menos uma taxa ou desmarque 'Reajustar taxas também'.");
      }
    }

    setSaving(true);
    try {
      const res = await api.post(`/frete/tabelas/${selectedId}/reajuste`, {
        nome: nomeNova.trim(),
        percentual: parseFloat(percentual),
        dataValIni,
        dataValFim,
        atualizarDataFimOrigem,
        reajusteTaxas,
      });
      onClose(res.data.id, res.data.anexos || null);
    } catch (e) {
      setErro(e.response?.data?.error || e.message || "Erro ao executar reajuste.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      toggle={() => onClose(null)}
      modalClassName="modal-align-top"
      fade={false}
      style={{ maxWidth: 680 }}
    >
      <div style={{
        background: "var(--everlog-card-surface-flat)",
        borderRadius: "0.4rem",
        border: "1px solid var(--everlog-card-border)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          background: "var(--theme-accent-glow)",
          padding: "0.9rem 1.25rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid var(--everlog-card-border)",
        }}>
          <span style={{ color: "var(--everlog-card-title)", fontWeight: 600, fontSize: "1rem" }}>
            <i className="tim-icons icon-refresh-02" style={{ marginRight: 8 }} />
            Reajuste de Tabela de Frete
          </span>
          <button
            style={{ background: "none", border: "none", color: "var(--everlog-card-muted)", cursor: "pointer", fontSize: "1.2rem", lineHeight: 1 }}
            onClick={() => onClose(null)}
          >×</button>
        </div>

        <ModalBody style={{ padding: "1.25rem", background: "var(--everlog-card-surface-flat)" }}>

          {/* Passo 1 — Selecionar tabela */}
          <div style={{ marginBottom: "1.25rem" }}>
            <p style={{ color: "var(--everlog-card-muted)", fontSize: "0.82rem", marginBottom: "0.5rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              1. Selecionar tabela de origem
            </p>

            <Row style={{ marginBottom: "0.5rem" }}>
              <Col>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Input
                    type="text"
                    placeholder="Filtrar por nome..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && buscarTabelas(busca)}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <Button color="info" size="sm" onClick={() => buscarTabelas(busca)}>
                    {loadingTabelas ? <ClipLoader size={12} color="var(--everlog-on-accent-text)" /> : "Buscar"}
                  </Button>
                </div>
              </Col>
            </Row>

            <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--everlog-card-border)", borderRadius: 4 }}>
              {tabelas.length === 0 ? (
                <div style={{ padding: "1rem", textAlign: "center", color: "var(--everlog-card-muted)", fontSize: "0.85rem" }}>
                  Nenhuma tabela encontrada.
                </div>
              ) : tabelas.map((t) => (
                <div
                  key={t.id}
                  onClick={() => handleSelectTabela(t.id)}
                  style={{
                    padding: "0.5rem 0.75rem",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--everlog-card-border)",
                    background: String(selectedId) === String(t.id) ? "var(--theme-accent-glow)" : "transparent",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <span style={{ color: "var(--everlog-card-title)", fontSize: "0.88rem", fontWeight: String(selectedId) === String(t.id) ? 600 : 400 }}>
                      {t.nome}
                    </span>
                    <span style={{ color: "var(--everlog-card-muted)", fontSize: "0.78rem", marginLeft: 8 }}>
                      {t.transportadora || ""}
                    </span>
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "var(--everlog-card-muted)", textAlign: "right" }}>
                    {t.dataValFim
                      ? <span>Vigência até <strong style={{ color: "var(--everlog-card-title)" }}>{displayDate(t.dataValFim)}</strong></span>
                      : <span style={{ color: "var(--everlog-status-danger)" }}>Sem data final</span>}
                  </div>
                </div>
              ))}
            </div>

            {selectedTabela && (
              <div style={{ marginTop: "0.5rem", padding: "0.5rem 0.75rem", background: "var(--theme-accent-glow)", borderRadius: 4, border: "1px solid var(--everlog-card-border)", fontSize: "0.82rem" }}>
                <span style={{ color: "var(--everlog-card-muted)" }}>Selecionada: </span>
                <span style={{ color: "var(--everlog-card-title)", fontWeight: 600 }}>{selectedTabela.nome}</span>
                {selectedTabela.dataValFim
                  ? <span style={{ color: "var(--everlog-card-muted)", marginLeft: 8 }}>— vigência até <strong style={{ color: "var(--everlog-card-title)" }}>{displayDate(selectedTabela.dataValFim)}</strong></span>
                  : <span style={{ color: "var(--everlog-status-danger)", marginLeft: 8 }}>— sem data final de vigência</span>}
              </div>
            )}
            {selectedTabela && Array.isArray(anexosOrigem) && (
              <div style={{ marginTop: "0.4rem", color: "var(--everlog-card-muted)", fontSize: "0.8rem" }}>
                <i className="tim-icons icon-attach-87" style={{ marginRight: 6 }} />
                {anexosOrigem.length > 0
                  ? `${anexosOrigem.length} anexo(s) da tabela de origem serão copiados para a nova tabela.`
                  : "A tabela de origem não tem anexos."}
              </div>
            )}
          </div>

          {/* Passo 2 — Parâmetros do reajuste */}
          <div style={{ marginBottom: "1rem" }}>
            <p style={{ color: "var(--everlog-card-muted)", fontSize: "0.82rem", marginBottom: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              2. Parâmetros do reajuste
            </p>

            <Row>
              <Col md="12">
                <FormGroup style={{ marginBottom: "0.75rem" }}>
                  <Label style={labelStyle}>Nome da nova tabela<span style={{ color: "var(--everlog-status-danger)" }}> *</span></Label>
                  <Input
                    type="text"
                    placeholder="Nome da nova tabela..."
                    value={nomeNova}
                    onChange={(e) => setNomeNova(e.target.value)}
                    style={inputStyle}
                    disabled={paramsLocked}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="4">
                <FormGroup style={{ marginBottom: "0.75rem" }}>
                  <Label style={labelStyle}>Percentual de reajuste (%)<span style={{ color: "var(--everlog-status-danger)" }}> *</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 11"
                    value={percentual}
                    onChange={(e) => setPercentual(e.target.value)}
                    style={inputStyle}
                    disabled={paramsLocked}
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup style={{ marginBottom: "0.75rem" }}>
                  <Label style={labelStyle}>Data inicial da nova tabela<span style={{ color: "var(--everlog-status-danger)" }}> *</span></Label>
                  <Input
                    type="date"
                    value={dataValIni}
                    onChange={(e) => {
                      setDataValIni(e.target.value);
                      setAtualizarDataFimOrigem(false);
                    }}
                    style={inputStyle}
                    disabled={paramsLocked}
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup style={{ marginBottom: "0.75rem" }}>
                  <Label style={labelStyle}>Data final da nova tabela<span style={{ color: "var(--everlog-status-danger)" }}> *</span></Label>
                  <Input
                    type="date"
                    value={dataValFim}
                    onChange={(e) => setDataValFim(e.target.value)}
                    style={inputStyle}
                    min={dataValIni || undefined}
                    disabled={paramsLocked}
                  />
                  {dataFinalInvalida && (
                    <div style={{ color: "var(--everlog-status-danger)", fontSize: "0.76rem", marginTop: "0.25rem" }}>
                      A data final deve ser igual ou posterior a {displayDate(dataValIni)}.
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {selectedTabela && dataValIni && novaDataFimOrigem !== formatDate(selectedTabela.dataValFim) && (
              <div style={{ marginBottom: "0.75rem", padding: "0.6rem 0.75rem", background: "var(--theme-accent-glow)", border: "1px solid var(--theme-accent-border)", borderRadius: 4 }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", cursor: "pointer", marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={atualizarDataFimOrigem}
                    onChange={(e) => setAtualizarDataFimOrigem(e.target.checked)}
                    style={{ marginTop: "0.15rem", width: 15, height: 15, accentColor: "var(--theme-accent)", flexShrink: 0 }}
                  />
                  <span style={{ color: "var(--everlog-card-text)", fontSize: "0.85rem", lineHeight: 1.4 }}>
                    Atualizar data final da tabela de origem <strong style={{ color: "var(--everlog-card-title)" }}>"{selectedTabela.nome}"</strong> de{" "}
                    <strong style={{ color: "var(--everlog-card-title)" }}>{displayDate(selectedTabela.dataValFim)}</strong> para{" "}
                    <strong style={{ color: "var(--theme-accent)" }}>{displayDate(novaDataFimOrigem)}</strong>
                  </span>
                </label>
              </div>
            )}

            {selectionBlocked && (
              <div style={{ padding: "0.6rem 0.75rem", background: "var(--everlog-status-danger-bg)", border: "1px solid var(--everlog-status-danger-border)", borderRadius: 4, fontSize: "0.82rem", color: "var(--everlog-status-danger)" }}>
                A tabela selecionada não possui data final de vigência. Corrija a tabela de origem antes de executar o reajuste.
              </div>
            )}

            {percentual !== "" && dataValIni && dataValFim && !dataFinalInvalida && (
              <div style={{ padding: "0.6rem 0.75rem", background: "var(--everlog-status-success-bg)", border: "1px solid var(--everlog-status-success-border)", borderRadius: 4, fontSize: "0.82rem", color: "var(--everlog-status-success)" }}>
                Será criada uma nova tabela com custos reajustados em <strong>{percentual}%</strong>,
                com vigência de <strong>{displayDate(dataValIni)}</strong> até <strong>{displayDate(dataValFim)}</strong>.
                {!reajustarTaxas && " As taxas serão copiadas sem alteração de valor."}
                {atualizarDataFimOrigem && novaDataFimOrigem && (
                  <span> A tabela de origem terá sua data final atualizada para <strong>{displayDate(novaDataFimOrigem)}</strong>.</span>
                )}
              </div>
            )}
          </div>

          {/* Passo 3 — Reajuste de taxas */}
          {!paramsLocked && taxasDisponiveis.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <p style={{ color: "var(--everlog-card-muted)", fontSize: "0.82rem", marginBottom: "0.5rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                3. Reajuste de taxas (opcional)
              </p>

              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", marginBottom: "0.5rem" }}>
                <input
                  type="checkbox"
                  checked={reajustarTaxas}
                  onChange={(e) => setReajustarTaxas(e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: "var(--theme-accent)" }}
                />
                <span style={{ color: "var(--everlog-card-text)", fontSize: "0.88rem" }}>
                  Reajustar taxas também
                </span>
              </label>

              {reajustarTaxas && (
                <div style={{ padding: "0.75rem", background: "var(--everlog-card-surface-field)", border: "1px solid var(--everlog-card-border)", borderRadius: 4 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <span style={{ color: "var(--everlog-card-muted)", fontSize: "0.78rem" }}>
                      {taxasDisponiveis.length} taxa(s) nesta tabela. % em branco herda o percentual dos custos.
                    </span>
                    <div style={{ display: "flex", gap: "0.4rem" }}>
                      <Button color="link" size="sm" style={{ padding: 0, fontSize: "0.76rem" }} onClick={() => toggleTodasTaxas(true)}>
                        Marcar todas
                      </Button>
                      <span style={{ color: "var(--everlog-card-text-softer)" }}>|</span>
                      <Button color="link" size="sm" style={{ padding: 0, fontSize: "0.76rem" }} onClick={() => toggleTodasTaxas(false)}>
                        Desmarcar todas
                      </Button>
                      <span style={{ color: "var(--everlog-card-text-softer)" }}>|</span>
                      <Button color="link" size="sm" style={{ padding: 0, fontSize: "0.76rem" }} onClick={aplicarPercCustosTodasTaxas}>
                        Usar % dos custos em todas
                      </Button>
                    </div>
                  </div>

                  <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--everlog-card-border)", borderRadius: 4 }}>
                    {taxasDisponiveis.map((tx) => {
                      const cfg = taxaConfig[tx.value] || { selected: true, percentual: "" };
                      return (
                        <div key={tx.value} style={{
                          display: "flex", alignItems: "center", gap: "0.5rem",
                          padding: "0.4rem 0.6rem", borderBottom: "1px solid var(--everlog-card-border)",
                        }}>
                          <input
                            type="checkbox"
                            checked={!!cfg.selected}
                            onChange={(e) => setTaxaSelected(tx.value, e.target.checked)}
                            style={{ width: 14, height: 14, accentColor: "var(--theme-accent)" }}
                          />
                          <span style={{ color: "var(--everlog-card-title)", fontSize: "0.85rem", flex: 1 }}>
                            {tx.label}
                            <span style={{ color: "var(--everlog-card-muted)", fontSize: "0.76rem", marginLeft: 6 }}>
                              ({tx.count} linha{tx.count === 1 ? "" : "s"})
                            </span>
                          </span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder={percentual !== "" ? `${percentual}%` : "% custos"}
                            value={cfg.percentual}
                            onChange={(e) => setTaxaPercentual(tx.value, e.target.value)}
                            disabled={!cfg.selected}
                            style={{ ...inputStyle, width: 90, padding: "0.2rem 0.4rem", fontSize: "0.82rem" }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {loadingTaxas && (
            <div style={{ padding: "0.5rem", fontSize: "0.82rem", color: "var(--everlog-card-muted)" }}>
              <ClipLoader size={10} color="var(--everlog-card-muted)" /> Carregando taxas da tabela...
            </div>
          )}

          {erro && (
            <div style={{ background: "var(--everlog-status-danger)", color: "var(--everlog-on-accent-text)", padding: "0.6rem 0.75rem", borderRadius: 4, marginBottom: "1rem", fontSize: "0.85rem" }}>
              {erro}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", borderTop: "1px solid var(--everlog-card-border)", paddingTop: "1rem" }}>
            <Button color="secondary" size="sm" onClick={() => onClose(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" size="sm" onClick={handleReajustar} disabled={!canExecute}>
              {saving ? <ClipLoader size={12} color="var(--everlog-on-accent-text)" style={{ marginRight: 6 }} /> : null}
              {saving ? "Executando..." : "Executar Reajuste"}
            </Button>
          </div>
        </ModalBody>
      </div>
    </Modal>
  );
}
